/**
 * In-memory live state for BiQ turns, keyed by conversation id.
 *
 * A stream writes only into its own chat's entry, so generation can continue
 * while the user looks at another chat. React bindings live in
 * BiqLiveChatsContext.jsx; this module is the store itself so it can be
 * unit-tested without the component tree.
 */

/** Max turns generating at once per user (each holds a backend worker thread). */
export const MAX_CONCURRENT_TURNS = 3;

/** Idle (not generating) chats kept in memory after the user leaves them. */
export const MAX_IDLE_ENTRIES = 10;

export const createEntry = () => ({
  messages: [],
  isStreaming: false,
  statusMessage: '',
  pdfDownload: null,
  pdfDownloadMessageId: null,
  contextLevel: null,
  contextFullInfo: null,
  isGeneratingReport: false,
  // Synchronous duplicate-send lock (KAN-249), now per chat.
  isSending: false,
  abortController: null,
  cancelToken: null,
  unread: false,
});

// Returned for chats without an entry. Frozen + shared so snapshots stay stable.
export const EMPTY_LIVE_CHAT = Object.freeze(createEntry());

export const isBusy = (entry) =>
  Boolean(entry?.isStreaming || entry?.isSending || entry?.isGeneratingReport);

export const createLiveChatsStore = (options = {}) => {
  const onCancel = options.onCancel || (() => {});
  const entries = new Map();
  // previousId -> nextId, for chats the backend re-keyed mid-turn. Lets
  // callbacks that captured the old id keep writing to the right entry.
  const aliases = new Map();
  const listeners = new Set();
  let busyIds = [];
  let unreadIds = [];

  const resolve = (id) => {
    let current = id;
    let hops = 0;
    while (aliases.has(current) && hops < 10) {
      current = aliases.get(current);
      hops += 1;
    }
    return current;
  };

  const sameIdList = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

  const recomputeLists = () => {
    const nextBusy = [];
    const nextUnread = [];
    entries.forEach((entry, id) => {
      if (isBusy(entry)) nextBusy.push(id);
      if (entry.unread) nextUnread.push(id);
    });
    if (!sameIdList(nextBusy, busyIds)) busyIds = nextBusy;
    if (!sameIdList(nextUnread, unreadIds)) unreadIds = nextUnread;
  };

  const emit = () => {
    recomputeLists();
    listeners.forEach((listener) => listener());
  };

  const get = (id) => (id ? entries.get(resolve(id)) || EMPTY_LIVE_CHAT : EMPTY_LIVE_CHAT);

  const ensure = (id) => {
    if (!id) return;
    const key = resolve(id);
    if (!entries.has(key)) {
      entries.set(key, createEntry());
      emit();
    }
  };

  // Mirrors useState's setter: accepts a value or an updater. No-op when the
  // chat has no entry, so a late callback can't resurrect an evicted chat.
  const set = (id, key, valueOrUpdater) => {
    if (!id) return;
    const resolved = resolve(id);
    const entry = entries.get(resolved);
    if (!entry) return;
    const value =
      typeof valueOrUpdater === 'function' ? valueOrUpdater(entry[key]) : valueOrUpdater;
    if (Object.is(value, entry[key])) return;
    entries.set(resolved, { ...entry, [key]: value });
    emit();
  };

  const patch = (id, partial) => {
    if (!id) return;
    const resolved = resolve(id);
    const entry = entries.get(resolved);
    if (!entry) return;
    entries.set(resolved, { ...entry, ...partial });
    emit();
  };

  const rekey = (previousId, nextId) => {
    if (!previousId || !nextId || previousId === nextId) return;
    const from = resolve(previousId);
    const to = resolve(nextId);
    if (from === to) return;
    const entry = entries.get(from);
    aliases.set(from, to);
    if (entry) {
      entries.delete(from);
      entries.set(to, entry);
    }
    emit();
  };

  const abort = (id) => {
    const entry = entries.get(resolve(id));
    if (!entry) return;
    const { abortController, cancelToken } = entry;
    if (abortController) abortController.abort();
    if (cancelToken) onCancel(cancelToken);
    patch(id, { abortController: null, cancelToken: null, isSending: false });
  };

  /** Abort every turn and forget all chats (account switch, leaving the app shell). */
  const clear = () => {
    Array.from(entries.keys()).forEach(abort);
    entries.clear();
    aliases.clear();
    emit();
  };

  /**
   * Drop idle chats beyond MAX_IDLE_ENTRIES. Never drops `keepId` or a
   * chat that is still generating.
   */
  const evictIdle = (keepId) => {
    const keep = keepId ? resolve(keepId) : null;
    const idle = [];
    entries.forEach((entry, chatId) => {
      if (chatId !== keep && !isBusy(entry)) idle.push(chatId);
    });
    const overflow = idle.length - MAX_IDLE_ENTRIES;
    if (overflow <= 0) return;
    // Map iteration order is insertion order, so the oldest idle chats go first.
    idle.slice(0, overflow).forEach((chatId) => entries.delete(chatId));
    emit();
  };

  const countBusy = (exceptId) => {
    const except = exceptId ? resolve(exceptId) : null;
    return busyIds.filter((chatId) => chatId !== except).length;
  };

  return {
    resolve,
    get,
    ensure,
    set,
    patch,
    rekey,
    abort,
    clear,
    evictIdle,
    countBusy,
    getBusyIds: () => busyIds,
    getUnreadIds: () => unreadIds,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};
